import { useState, useRef, useEffect } from "react";
import { baseApiClient } from "data/modules/api/api.client";
import { api } from "data";
import {
  useNotifications,
  notification_color_keys,
} from "utils/hooks/useNotifications";
import { isRequestSuccessful } from "utils/Utils";
import { logError } from "utils/error";
import { is_empty } from "features/Common/utils/common.utils";
import { RECORDING_SETTINGS_MODAL_CONSTANTS } from "../../../constants/ManageRecordings.constant";

/**
 * Custom hook to manage state, listing details fetching, and save operations for RecordingSettingsModal.
 *
 * @param {Object} params
 * @param {Object} [params.formProps] - Fallback form props passed from parent components
 * @param {string} [params.listingUuid] - Active listing/offering UUID
 * @param {boolean} params.openModal - Active modal visibility state
 * @param {Function} params.onClose - Callback function to close the modal
 */
export const useRecordingSettingsModal = ({
  formProps,
  listingUuid,
  openModal,
  onClose,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [listingDetails, setListingDetails] = useState(null);
  const shareRecordingRef = useRef(null);
  const { notify } = useNotifications();

  // Resolves target listing UUID (from explicit prop or formProps)
  const targetUuid = listingUuid || formProps?.listingUuid;

  // Fetches current listing details from API whenever the modal is opened
  useEffect(() => {
    let isCancelled = false;

    if (openModal) {
      if (is_empty(targetUuid)) {
        notify(
          RECORDING_SETTINGS_MODAL_CONSTANTS.ERROR_MESSAGE,
          notification_color_keys.error
        );
        logError({
          error: new Error("Missing targetUuid in RecordingSettingsModal"),
          occuredAt:
            "useEffect fetchListingDetails in src/ui/pages/schedule/ManageOffering/pages/ManageRecordings/components/RecordingSettingsModal/hooks/useRecordingSettingsModal.js",
          when: "when modal is opened without a valid targetUuid",
        });
        return;
      }

      const fetchListingDetails = async () => {
        try {
          setIsLoadingDetails(true);
          const response = await baseApiClient.get(
            `${api.get_listing_details}/${targetUuid}`
          );
          if (!isCancelled && isRequestSuccessful(response?.status)) {
            const fetchedEvent =
              response?.data?.data?.event || response?.data?.data;
            setListingDetails(fetchedEvent);
          } else if (!isCancelled) {
            notify(
              RECORDING_SETTINGS_MODAL_CONSTANTS.ERROR_MESSAGE,
              notification_color_keys.error
            );
          }
        } catch (err) {
          if (!isCancelled) {
            notify(
              RECORDING_SETTINGS_MODAL_CONSTANTS.ERROR_MESSAGE,
              notification_color_keys.error
            );
            logError({
              error: err,
              occuredAt:
                "fetchListingDetails in src/ui/pages/schedule/ManageOffering/pages/ManageRecordings/components/RecordingSettingsModal/hooks/useRecordingSettingsModal.js",
              when: "when fetching listing details for recording settings modal",
            });
          }
        } finally {
          if (!isCancelled) {
            setIsLoadingDetails(false);
          }
        }
      };
      fetchListingDetails();
    } else {
      setListingDetails(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [openModal, targetUuid]);

  /**
   * Submits updated recording settings to host/v2/listing/update API endpoint.
   */
  const handleSaveDetails = async () => {
    if (isSaving) return;

    if (is_empty(targetUuid)) {
      notify(
        RECORDING_SETTINGS_MODAL_CONSTANTS.ERROR_MESSAGE,
        notification_color_keys.error
      );
      logError({
        error: new Error("Missing targetUuid in handleSaveDetails"),
        occuredAt:
          "handleSaveDetails in src/ui/pages/schedule/ManageOffering/pages/ManageRecordings/components/RecordingSettingsModal/hooks/useRecordingSettingsModal.js",
        when: "when saving recording details without a valid targetUuid",
      });
      return;
    }

    try {
      setIsSaving(true);
      const values = shareRecordingRef.current?.getValues?.() || {};

      const response = await baseApiClient.post(api.update_listing, {
        uuid: targetUuid,
        updated_values: values,
      });

      if (isRequestSuccessful(response?.status)) {
        notify(
          RECORDING_SETTINGS_MODAL_CONSTANTS.SUCCESS_MESSAGE,
          notification_color_keys.success
        );
        onClose();
      } else {
        notify(
          RECORDING_SETTINGS_MODAL_CONSTANTS.ERROR_MESSAGE,
          notification_color_keys.error
        );
        logError({
          error: new Error(
            response?.data?.message || "Failed to save recording settings"
          ),
          occuredAt:
            "handleSaveDetails API status check in src/ui/pages/schedule/ManageOffering/pages/ManageRecordings/components/RecordingSettingsModal/hooks/useRecordingSettingsModal.js",
          when: "when API returns non-successful status code on save recording details",
        });
      }
    } catch (err) {
      notify(
        RECORDING_SETTINGS_MODAL_CONSTANTS.ERROR_MESSAGE,
        notification_color_keys.error
      );
      logError({
        error: err,
        occuredAt:
          "handleSaveDetails catch block in src/ui/pages/schedule/ManageOffering/pages/ManageRecordings/components/RecordingSettingsModal/hooks/useRecordingSettingsModal.js",
        when: "when saving recording details in recording settings modal",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Computes initial values prioritizing API fetched details with fallback to formProps
  const isAutoRecordableVal =
    listingDetails?.is_auto_recordable !== undefined
      ? listingDetails?.is_auto_recordable
      : formProps?.is_auto_recordable;

  const mapToCourseVal =
    listingDetails?.map_to_course !== undefined
      ? listingDetails?.map_to_course
      : formProps?.map_to_course;

  const recordingAccessibilityVal =
    listingDetails?.recording_accessibility !== undefined
      ? listingDetails?.recording_accessibility
      : formProps?.recording_accessibility;

  const addressTypeVal =
    listingDetails?.address_type ?? formProps?.address_type;
  const typeVal = listingDetails?.type ?? formProps?.type;

  return {
    isSaving,
    isLoadingDetails,
    listingDetails,
    targetUuid,
    shareRecordingRef,
    handleSaveDetails,
    isAutoRecordableVal,
    mapToCourseVal,
    recordingAccessibilityVal,
    addressTypeVal,
    typeVal,
  };
};
